import { 
  sendPasswordResetEmail, 
  createUserWithEmailAndPassword
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase';
import { UserRepository } from '../repository/user.repository';
import type { AdminAuditRepository } from '../repository/admin-audit.repository';
import type { AdminAuditLogEntry } from '../repository/admin-audit.repository';
import { CreateUserData, GetUserData } from '../repository/interfaces';

export interface AdminPasswordResetRequest {
  userId: string;
  email?: string;
  username?: string;
  adminUserId: string;
  reason?: string;
}

export interface UsernameAvailabilityResult {
  available: boolean;
  message: string;
  suggestions?: string[];
}

export interface AdminUserCreationData {
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  password?: string; // Optional - if not provided, will trigger password reset email
  roles: string[];
  subscriptionTier: string;
  adminUserId: string;
  sendWelcomeEmail?: boolean;
}

export interface AdminUserUpdateData {
  userId: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  subscriptionTier?: string;
  isActive?: boolean;
  adminUserId: string;
  reason?: string;
}

export type AdminActionLog = AdminAuditLogEntry;

/**
 * Comprehensive Admin User Management Service
 * Handles all admin-level user management operations with proper logging and security
 */
export class AdminUserManagementService {
  constructor(
    private userRepo: UserRepository,
    private auditRepo: AdminAuditRepository,
  ) {}

  /**
   * Send password reset email for a user (Admin initiated)
   * Can be triggered from admin panel user management
   */
  async sendPasswordResetEmail(request: AdminPasswordResetRequest): Promise<{
    success: boolean;
    message: string;
    actionLogId?: string;
  }> {
    console.log('[AdminUserManagementService.sendPasswordResetEmail] START:', request);
    
    try {
      // Get user data
      let userData: GetUserData | null = null;
      
      if (request.userId) {
        userData = await this.userRepo.getUserById(request.userId);
      } else if (request.email) {
        const users = await this.userRepo.getUsersByEmail(request.email);
        userData = users.length > 0 ? users[0] : null;
      } else if (request.username) {
        userData = await this.userRepo.getUserByUsername(request.username);
      }

      if (!userData) {
        throw new Error('User not found');
      }

      // Send password reset email via Firebase Auth
      await sendPasswordResetEmail(auth, userData.email);

      // Log admin action
      const actionLog: AdminActionLog = {
        adminUserId: request.adminUserId,
        targetUserId: userData.id,
        action: 'PASSWORD_RESET',
        details: {
          method: request.userId ? 'userId' : request.email ? 'email' : 'username',
          reason: request.reason || 'Admin initiated password reset',
          targetEmail: userData.email,
          targetUsername: userData.username
        },
        timestamp: new Date()
      };

      const actionLogRef = await this.logAdminAction(actionLog);

      console.log('[AdminUserManagementService.sendPasswordResetEmail] SUCCESS');
      return {
        success: true,
        message: `Password reset email sent to ${userData.email}`,
        actionLogId: actionLogRef.id
      };

    } catch (error) {
      console.error('[AdminUserManagementService.sendPasswordResetEmail] ERROR:', error);
      throw new Error(`Failed to send password reset email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check username availability with smart suggestions
   * Enhanced version for registration page
   */
  async checkUsernameAvailability(username: string, excludeUserId?: string): Promise<UsernameAvailabilityResult> {
    console.log('[AdminUserManagementService.checkUsernameAvailability] START:', username);
    
    try {
      if (!username || username.length < 3) {
        return {
          available: false,
          message: 'Username must be at least 3 characters long'
        };
      }

      if (!/^[a-zA-Z0-9_]+$/.test(username)) {
        return {
          available: false,
          message: 'Username can only contain letters, numbers, and underscores'
        };
      }

      // Check if username is available
      const isAvailable = await this.userRepo.isUsernameAvailable(username);
      
      if (isAvailable) {
        return {
          available: true,
          message: '✓ Username is available'
        };
      } else {
        // Generate suggestions if username is taken
        const suggestions = await this.generateUsernameSuggestions(username);
        return {
          available: false,
          message: '✗ Username is already taken',
          suggestions
        };
      }

    } catch (error) {
      console.error('[AdminUserManagementService.checkUsernameAvailability] ERROR:', error);
      return {
        available: false,
        message: 'Error checking username availability'
      };
    }
  }

  /**
   * Enhanced forgot password that supports email OR username
   * For use on auth page
   */
  async forgotPasswordByEmailOrUsername(emailOrUsername: string): Promise<{
    success: boolean;
    message: string;
  }> {
    console.log('[AdminUserManagementService.forgotPasswordByEmailOrUsername] START:', emailOrUsername);
    
    try {
      const normalizedInput = emailOrUsername.trim();
      if (!normalizedInput) {
        return {
          success: true,
          message: 'If an account exists with that email or username, a password reset link has been sent.'
        };
      }

      // If user entered an email, try Firebase Auth reset directly.
      if (normalizedInput.includes('@')) {
        try {
          await sendPasswordResetEmail(auth, normalizedInput.toLowerCase());
        } catch (error: any) {
          // Keep response generic for security; only unexpected auth issues should bubble.
          if (error?.code && error.code !== 'auth/user-not-found') {
            throw error;
          }
        }

        return {
          success: true,
          message: 'If an account exists with that email or username, a password reset link has been sent.'
        };
      }

      let userData: GetUserData | null = null;

      // Username lookups can fail on case differences, so try common variants.
      const usernameCandidates = [
        normalizedInput,
        normalizedInput.toLowerCase(),
        normalizedInput.toUpperCase()
      ];

      for (const candidate of usernameCandidates) {
        userData = await this.userRepo.getUserByUsername(candidate);
        if (userData) {
          break;
        }
      }

      if (!userData) {
        // Don't reveal if user exists for security - always return success message
        return {
          success: true,
          message: 'If an account exists with that email or username, a password reset link has been sent.'
        };
      }

      // Send password reset email
      try {
        await sendPasswordResetEmail(auth, userData.email);
      } catch (error: any) {
        if (error?.code && error.code !== 'auth/user-not-found') {
          throw error;
        }
      }

      console.log('[AdminUserManagementService.forgotPasswordByEmailOrUsername] SUCCESS');
      return {
        success: true,
        message: 'If an account exists with that email or username, a password reset link has been sent.'
      };

    } catch (error) {
      console.error('[AdminUserManagementService.forgotPasswordByEmailOrUsername] ERROR:', error);
      // Still return success for security
      return {
        success: true,
        message: 'If an account exists with that email or username, a password reset link has been sent.'
      };
    }
  }

  /**
   * Trigger password reset for profile page
   * Send password reset email to current user
   */
  async sendPasswordResetToCurrentUser(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    console.log('[AdminUserManagementService.sendPasswordResetToCurrentUser] START:', userId);
    
    try {
      const userData = await this.userRepo.getUserById(userId);
      if (!userData) {
        throw new Error('User not found');
      }

      // Send password reset email
      await sendPasswordResetEmail(auth, userData.email);

      console.log('[AdminUserManagementService.sendPasswordResetToCurrentUser] SUCCESS');
      return {
        success: true,
        message: `Password reset email sent to ${userData.email}`
      };

    } catch (error) {
      console.error('[AdminUserManagementService.sendPasswordResetToCurrentUser] ERROR:', error);
      throw new Error(`Failed to send password reset email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Create new user from admin panel
   * With optional password or password reset email
   */
  async createUserAsAdmin(userData: AdminUserCreationData): Promise<{
    success: boolean;
    user: GetUserData;
    passwordResetSent: boolean;
    actionLogId: string;
  }> {
    console.log('[AdminUserManagementService.createUserAsAdmin] START:', userData);
    
    try {
      // Check if username is available
      const usernameCheck = await this.checkUsernameAvailability(userData.username);
      if (!usernameCheck.available) {
        throw new Error(`Username not available: ${usernameCheck.message}`);
      }

      // Check if email is already in use
      const existingUsers = await this.userRepo.getUsersByEmail(userData.email);
      if (existingUsers.length > 0) {
        throw new Error('Email is already in use');
      }

      const tempPassword = userData.password || this.generateTemporaryPassword();
      
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, tempPassword);
      
      // Create user profile in Firestore
      const createUserData: CreateUserData = {
        email: userData.email,
        username: userData.username,
        password: tempPassword,
        firstName: userData.firstName,
        lastName: userData.lastName,
        roles: userData.roles,
        subscriptionTier: userData.subscriptionTier,
        isActive: true,
        emailVerified: false
      };

      const newUser = await this.userRepo.createUser(createUserData);

      let passwordResetSent = false;
      
      // If no password was provided, send password reset email
      if (!userData.password) {
        await sendPasswordResetEmail(auth, userData.email);
        passwordResetSent = true;
      }

      // Log admin action
      const actionLog: AdminActionLog = {
        adminUserId: userData.adminUserId,
        targetUserId: newUser.id,
        action: 'USER_CREATED',
        details: {
          email: userData.email,
          username: userData.username,
          roles: userData.roles,
          subscriptionTier: userData.subscriptionTier,
          passwordResetSent,
          sendWelcomeEmail: userData.sendWelcomeEmail
        },
        timestamp: new Date()
      };

      const actionLogRef = await this.logAdminAction(actionLog);

      console.log('[AdminUserManagementService.createUserAsAdmin] SUCCESS');
      return {
        success: true,
        user: newUser,
        passwordResetSent,
        actionLogId: actionLogRef.id
      };

    } catch (error) {
      console.error('[AdminUserManagementService.createUserAsAdmin] ERROR:', error);
      throw new Error(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Bulk user operations
   */
  async bulkUpdateUsers(updates: AdminUserUpdateData[]): Promise<{
    success: boolean;
    results: Array<{ userId: string; success: boolean; error?: string }>;
  }> {
    console.log('[AdminUserManagementService.bulkUpdateUsers] START:', updates.length, 'updates');
    
    const results: Array<{ userId: string; success: boolean; error?: string }> = [];
    
    for (const update of updates) {
      try {
        await this.updateUserAsAdmin(update);
        results.push({ userId: update.userId, success: true });
      } catch (error) {
        results.push({ 
          userId: update.userId, 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    const successCount = results.filter(r => r.success).length;
    console.log('[AdminUserManagementService.bulkUpdateUsers] SUCCESS:', successCount, '/', updates.length);
    
    return {
      success: successCount > 0,
      results
    };
  }

  /**
   * Update user as admin
   */
  async updateUserAsAdmin(updateData: AdminUserUpdateData): Promise<{
    success: boolean;
    user: GetUserData;
    actionLogId: string;
  }> {
    console.log('[AdminUserManagementService.updateUserAsAdmin] START:', updateData);
    
    try {
      const existingUser = await this.userRepo.getUserById(updateData.userId);
      if (!existingUser) {
        throw new Error('User not found');
      }

      // Check username availability if username is being changed
      if (updateData.username && updateData.username !== existingUser.username) {
        const usernameCheck = await this.checkUsernameAvailability(updateData.username, updateData.userId);
        if (!usernameCheck.available) {
          throw new Error(`Username not available: ${usernameCheck.message}`);
        }
      }

      // Update user data (profile + roles only). Subscription tier/status belongs in `subscriptions/{uid}`, not `users`.
      const updatedUser = await this.userRepo.updateUser(updateData.userId, {
        username: updateData.username,
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        roles: updateData.roles,
        isActive: updateData.isActive,
        updatedAt: new Date()
      });

      // Log admin action
      const actionLog: AdminActionLog = {
        adminUserId: updateData.adminUserId,
        targetUserId: updateData.userId,
        action: 'USER_UPDATED',
        details: {
          changes: updateData,
          reason: updateData.reason || 'Admin update',
          previousData: {
            email: existingUser.email,
            username: existingUser.username,
            roles: existingUser.roles,
            subscriptionTier: existingUser.subscriptionTier,
            isActive: existingUser.isActive
          }
        },
        timestamp: new Date()
      };

      const actionLogRef = await this.logAdminAction(actionLog);

      console.log('[AdminUserManagementService.updateUserAsAdmin] SUCCESS');
      return {
        success: true,
        user: updatedUser,
        actionLogId: actionLogRef.id
      };

    } catch (error) {
      console.error('[AdminUserManagementService.updateUserAsAdmin] ERROR:', error);
      throw new Error(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get admin action logs
   */
  async getAdminActionLogs(filters: {
    adminUserId?: string;
    targetUserId?: string;
    action?: string;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
  } = {}): Promise<AdminActionLog[]> {
    console.log('[AdminUserManagementService.getAdminActionLogs] START:', filters);
    
    try {
      const logs = await this.auditRepo.queryLogs({
        adminUserId: filters.adminUserId,
        targetUserId: filters.targetUserId,
        action: filters.action,
        limit: filters.limit ? Math.min(filters.limit * 4, 500) : undefined,
      });

      let filtered = logs;
      if (filters.startDate) {
        filtered = filtered.filter((l) => l.timestamp >= filters.startDate!);
      }
      if (filters.endDate) {
        filtered = filtered.filter((l) => l.timestamp <= filters.endDate!);
      }
      if (filters.limit) {
        filtered = filtered.slice(0, filters.limit);
      }

      console.log('[AdminUserManagementService.getAdminActionLogs] SUCCESS:', filtered.length, 'logs');
      return filtered;

    } catch (error) {
      console.error('[AdminUserManagementService.getAdminActionLogs] ERROR:', error);
      throw new Error('Failed to fetch admin action logs');
    }
  }

  /**
   * Generate smart username suggestions
   */
  private async generateUsernameSuggestions(baseUsername: string): Promise<string[]> {
    const suggestions: string[] = [];
    
    // Try with numbers
    for (let i = 1; i <= 99; i++) {
      const suggestion = `${baseUsername}${i}`;
      const isAvailable = await this.userRepo.isUsernameAvailable(suggestion);
      if (isAvailable) {
        suggestions.push(suggestion);
        if (suggestions.length >= 3) break;
      }
    }
    
    // Try with underscores and variations
    const variations = [
      `${baseUsername}_user`,
      `${baseUsername}_pro`,
      `${baseUsername}_dance`,
      `user_${baseUsername}`,
      `${baseUsername}x`,
      `${baseUsername}xx`
    ];
    
    for (const variation of variations) {
      if (suggestions.length >= 5) break;
      const isAvailable = await this.userRepo.isUsernameAvailable(variation);
      if (isAvailable && !suggestions.includes(variation)) {
        suggestions.push(variation);
      }
    }
    
    return suggestions.slice(0, 5);
  }

  /**
   * Generate temporary password
   */
  private generateTemporaryPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  /**
   * Log admin action for audit trail
   */
  private async logAdminAction(actionLog: AdminActionLog): Promise<{ id: string }> {
    try {
      const { id: _id, timestamp: _ts, ...rest } = actionLog;
      const result = await this.auditRepo.append(rest);

      console.log('[AdminUserManagementService.logAdminAction] Logged action:', actionLog.action, 'ID:', result.id);
      return result;

    } catch (error) {
      console.error('[AdminUserManagementService.logAdminAction] ERROR:', error);
      throw new Error('Failed to log admin action');
    }
  }

  /**
   * Send custom email to user (admin initiated)
   */
  async sendCustomEmailToUser(request: {
    userId: string;
    subject: string;
    message: string;
    adminUserId: string;
    template?: 'welcome' | 'notification' | 'warning' | 'custom';
  }): Promise<{
    success: boolean;
    message: string;
    actionLogId: string;
  }> {
    console.log('[AdminUserManagementService.sendCustomEmailToUser] START:', request);
    
    try {
      const userData = await this.userRepo.getUserById(request.userId);
      if (!userData) {
        throw new Error('User not found');
      }

      // Use cloud function to send email
      const sendEmailFunction = httpsCallable(functions, 'sendCustomEmail');
      await sendEmailFunction({
        to: userData.email,
        subject: request.subject,
        message: request.message,
        template: request.template || 'custom',
        userData: {
          firstName: userData.firstName,
          lastName: userData.lastName,
          username: userData.username
        }
      });

      // Log admin action
      const actionLog: AdminActionLog = {
        adminUserId: request.adminUserId,
        targetUserId: request.userId,
        action: 'EMAIL_SENT',
        details: {
          subject: request.subject,
          template: request.template,
          recipientEmail: userData.email,
          messageLength: request.message.length
        },
        timestamp: new Date()
      };

      const actionLogRef = await this.logAdminAction(actionLog);

      console.log('[AdminUserManagementService.sendCustomEmailToUser] SUCCESS');
      return {
        success: true,
        message: `Email sent to ${userData.email}`,
        actionLogId: actionLogRef.id
      };

    } catch (error) {
      console.error('[AdminUserManagementService.sendCustomEmailToUser] ERROR:', error);
      throw new Error(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
